import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'

import { log } from '@livekit/components-core'

import { useAppSelector, useAppDispatch } from '@/src/store/hooks'
import { selectPhone, selectReferrer, setPhone } from '@/src/store/slices/room'
import { setAccessToken } from '@/src/store/slices/token'
import {getInterviewAdmissionFailure, type InterviewAdmissionFailure} from '@/src/lib/interviewAdmission'

/** @public */
export interface UserInfo {
  identity?: string
  name?: string
  metadata?: string
  phone?: string
  [key: string]: string | number | boolean | null | undefined
}

/** @public */
export interface UseTokenOptions {
  userInfo?: UserInfo
  onAdmissionDenied?: (reason: InterviewAdmissionFailure) => void
}

/**
 * The `useToken` hook fetches a token from the given token endpoint with the given user info.
 *
 * @example
 * ```tsx
 * const token = useToken(<token-endpoint>, roomName, { userInfo: { identity, name }});
 * ```
 * @public */
export function useToken(
  tokenEndpoint: string | undefined,
  roomName: string,
  options: UseTokenOptions = {},
) {
  const [token, setToken] = useState<string | undefined>(undefined)
  const {userInfo, onAdmissionDenied} = options
  const { query } = useRouter()
  const isHrRoom = userInfo?.v === 'hr'
  const rawPhoneFromQuery = isHrRoom ? (query.user_phone ?? query.phone) : undefined
  const phone = useAppSelector(selectPhone)
  const referrer = useAppSelector(selectReferrer)
  const dispatch = useAppDispatch()

  useEffect(() => {
    let cancelled = false
    if (tokenEndpoint === undefined) {
      throw Error('token endpoint needs to be defined')
    }
    if (userInfo?.identity === undefined) {
      return
    }

    const tokenFetcher = async () => {
      log.debug('fetching token')
      const isElevatedParticipant = ['roomAdmin', 'isCurator', 'agent', 'recorder'].some((key) => {
        const value = userInfo?.[key]
        return value === true || value === 'true'
      })
      const userInfoParams = Object.entries(userInfo || {}).reduce<Record<string, string>>(
        (acc, [key, value]) => {
          if (value !== null && value !== undefined && (!isHrRoom || key !== 'phone')) {
            acc[key] = String(value)
          }

          return acc
        },
        {},
      )
      const phoneFromUserInfo = String(userInfo?.phone || '').trim()
      const phoneFromQuery =
        isHrRoom && !Array.isArray(rawPhoneFromQuery) ? String(rawPhoneFromQuery || '').trim() : ''
      const phoneFromStore = String(phone || '').trim()
      const tokenPhone = (!isHrRoom || !isElevatedParticipant)
        ? [phoneFromUserInfo, phoneFromQuery, phoneFromStore].find((value) => {
          const digitsCount = value.replace(/\D/g, '').length
          return isHrRoom ? digitsCount === 11 : digitsCount >= 11
        })
        : undefined

      if (isHrRoom && phoneFromStore !== (tokenPhone || '')) {
        dispatch(setPhone(tokenPhone || ''))
        return
      }

      const params = new URLSearchParams({
        roomName,
        ...userInfoParams,
        ...(tokenPhone && { phone: tokenPhone }),
        ...(referrer !== null && referrer !== undefined && { referrer }),
      })
      const res = await fetch(`${tokenEndpoint}?${params.toString()}`)

      if (!res.ok) {
        const result = await res.json().catch(() => ({}))
        const failure = getInterviewAdmissionFailure(result)
        if (!cancelled && failure) onAdmissionDenied?.(failure)
        log.error(
          `Could not fetch token. Server responded with status ${res.status}: ${res.statusText}`,
        )
        return
      }
      const { accessToken } = await res.json()
      if (cancelled) return
      dispatch(setAccessToken(accessToken))
      setToken(accessToken)
    }
    void tokenFetcher().catch((error) => {
      log.error('Could not fetch token', error)
      if (!cancelled && (isHrRoom || userInfo?.v === 'fa')) {
        onAdmissionDenied?.('check_unavailable')
      }
    })
    return () => { cancelled = true }
  }, [
    tokenEndpoint,
    roomName,
    userInfo,
    phone,
    referrer,
    dispatch,
    isHrRoom,
    rawPhoneFromQuery,
    onAdmissionDenied,
  ])

  return token
}
